import type { SessionView } from '@repwise/domain';
import type { SyncStatus } from '@repwise/sync';
import { useEffect, useRef, type ReactNode } from 'react';
import { NavLink } from 'react-router-dom';
import { mmss } from '../lib/copy.ts';

export function Button({ children, onClick, kind = 'primary', disabled, label }: { children: ReactNode; onClick?: () => void; kind?: 'primary' | 'secondary' | 'text'; disabled?: boolean; label?: string }) {
  const cls =
    kind === 'primary'
      ? 'bg-action text-on-action min-h-14 rounded-[20px] font-semibold text-lg w-full'
      : kind === 'secondary'
        ? 'border border-outline text-text min-h-12 rounded-[14px] px-4'
        : 'text-text-muted underline-offset-4 hover:underline min-h-11 px-2';
  return (
    <button type="button" className={`${cls} disabled:opacity-40 active:scale-[0.98] transition-transform`} onClick={onClick} disabled={disabled} aria-label={label}>
      {children}
    </button>
  );
}

/** C10 sync status. Quiet when synced; never alarming. */
export function SyncPill({ status, full = false }: { status: SyncStatus | null; full?: boolean }) {
  if (!status) return null;
  // The text only changes when the state really changes; an in-flight sync shows as a quiet dot (no flicker).
  const dot = status.syncing ? <span aria-hidden className="inline-block w-1.5 h-1.5 rounded-full bg-now mr-1.5 align-middle motion-safe:animate-pulse" /> : null;
  if (status.state === 'synced' && !full)
    return (
      <span className="text-text-muted text-sm" aria-label="Synced">
        {dot}✓ Synced
      </span>
    );
  const tone = status.state === 'attention' || status.state === 'needs_claim' ? 'text-sync-warn' : 'text-text-muted';
  // Headers get a short label so the screen title always fits; Settings shows the full sentence (C10).
  const short: Record<string, string> = { pending: `${status.pending} waiting`, offline: 'Offline', signed_out: 'Not signed in', needs_claim: 'Action needed', attention: 'Needs attention', synced: 'Synced', syncing: 'Syncing' };
  return (
    <span className={`${tone} text-sm whitespace-nowrap`} role="status" aria-live="polite" aria-label={status.label} title={status.label}>
      {dot}
      {full ? status.label : (short[status.state] ?? status.label)}
    </span>
  );
}

/** Outline icons on a 24 grid, 2 px stroke, currentColor (spec 01 §7). Decorative: labels carry the meaning. */
const Icon = ({ d, size = 24 }: { d: string[]; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
    {d.map((p, i) => (
      <path key={i} d={p} />
    ))}
  </svg>
);
export const ICONS = {
  train: ['M6 7v10', 'M18 7v10', 'M3 10v4', 'M21 10v4', 'M6 12h12'],
  progress: ['M4 19h16', 'M5 15l4-5 4 3 6-7'],
  history: ['M4 6h16', 'M4 12h16', 'M4 18h10'],
  settings: ['M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6z', 'M19.4 15a1.7 1.7 0 0 0 .3 1.8l.1.1a2 2 0 1 1-2.8 2.8l-.1-.1a1.7 1.7 0 0 0-1.8-.3 1.7 1.7 0 0 0-1 1.5V21a2 2 0 1 1-4 0v-.1a1.7 1.7 0 0 0-1.1-1.5 1.7 1.7 0 0 0-1.8.3l-.1.1a2 2 0 1 1-2.8-2.8l.1-.1a1.7 1.7 0 0 0 .3-1.8 1.7 1.7 0 0 0-1.5-1H3a2 2 0 1 1 0-4h.1a1.7 1.7 0 0 0 1.5-1.1 1.7 1.7 0 0 0-.3-1.8l-.1-.1a2 2 0 1 1 2.8-2.8l.1.1a1.7 1.7 0 0 0 1.8.3h0a1.7 1.7 0 0 0 1-1.5V3a2 2 0 1 1 4 0v.1a1.7 1.7 0 0 0 1 1.5 1.7 1.7 0 0 0 1.8-.3l.1-.1a2 2 0 1 1 2.8 2.8l-.1.1a1.7 1.7 0 0 0-.3 1.8v0a1.7 1.7 0 0 0 1.5 1H21a2 2 0 1 1 0 4h-.1a1.7 1.7 0 0 0-1.5 1z'],
  back: ['M15 18l-6-6 6-6'],
} as const;

export function IconGlyph({ name, size }: { name: keyof typeof ICONS; size?: number }) {
  return <Icon d={[...ICONS[name]]} {...(size ? { size } : {})} />;
}

/** C18: three tabs, icon + label; active = colour AND weight; hidden in a session and behind the gate. */
export function TabBar() {
  const tab = ({ isActive }: { isActive: boolean }) => `flex-1 min-h-14 pt-2 pb-1 flex flex-col items-center gap-0.5 text-xs ${isActive ? 'text-now-ink font-semibold' : 'text-text-muted'}`;
  const items = [
    { to: '/', end: true, label: 'Train', icon: 'train' as const },
    { to: '/progress', end: false, label: 'Progress', icon: 'progress' as const },
    { to: '/history', end: false, label: 'History', icon: 'history' as const },
  ];
  // Its height varies with text size and the phone's safe area (found on a Nokia XR20, 14 Oct 2026), so it publishes
  // it as --tabbar-h: the fixed Start bars on Ready sit exactly on top of it instead of guessing 4rem.
  const ref = useRef<HTMLElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const set = () => document.documentElement.style.setProperty('--tabbar-h', `${el.getBoundingClientRect().height}px`);
    set();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(set);
    ro?.observe(el);
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty('--tabbar-h');
    };
  }, []);
  return (
    <nav ref={ref} className="fixed bottom-0 inset-x-0 bg-surface border-t border-divider flex z-10" style={{ paddingBottom: 'env(safe-area-inset-bottom)' }} aria-label="Main">
      {items.map((it) => (
        <NavLink key={it.to} to={it.to} end={it.end} className={tab}>
          <IconGlyph name={it.icon} />
          {it.label}
        </NavLink>
      ))}
    </nav>
  );
}

/** Header for the three tabs: the Repwise mark, the screen title, and the Settings gear (J14b). */
export function TabHeader({ title, children }: { title: string; children?: ReactNode }) {
  return (
    // wraps rather than truncating the title at large text sizes (200%)
    <header className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
      <div className="flex items-center gap-2 min-w-0">
        <img src="/icons/mark.svg" alt="" width={28} height={23} className="shrink-0" />
        <h1 className="text-title num [overflow-wrap:anywhere]">{title}</h1>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        {children}
        <NavLink to="/settings" className="min-w-11 min-h-11 grid place-items-center rounded-full border border-outline text-text" aria-label="Settings">
          <IconGlyph name="settings" size={20} />
        </NavLink>
      </div>
    </header>
  );
}

/** C2 set circles: state by shape and numeral as well as colour (WCAG 1.4.1). */
export function Circles({ circles }: { circles: SessionView['circles'] }) {
  return (
    <ol className="flex gap-3 justify-center" aria-label="Work sets">
      {circles.map((c, i) => {
        const base = 'w-12 h-12 rounded-full grid place-items-center num text-xl';
        const style =
          c.state === 'done'
            ? 'bg-success text-on-action'
            : c.state === 'missed'
              ? 'bg-miss text-on-action [clip-path:polygon(0_0,100%_0,100%_75%,75%_100%,0_100%)]'
              : c.state === 'skipped'
                ? 'border-2 border-dashed border-outline text-text-muted'
                : c.state === 'current'
                  ? 'border-[3px] border-now-ink text-now-ink'
                  : 'border border-outline text-text-muted';
        const label = c.state === 'done' ? `Set ${i + 1}: ${c.reps} reps, done` : c.state === 'missed' ? `Set ${i + 1}: ${c.reps} reps, short` : c.state === 'skipped' ? `Set ${i + 1}: skipped` : `Set ${i + 1}: ${c.state}`;
        return (
          <li key={i} className={`${base} ${style}`} aria-label={label}>
            {c.state === 'done' ? `✓${c.reps}` : c.state === 'skipped' ? '–' : (c.reps ?? '')}
          </li>
        );
      })}
    </ol>
  );
}

/** C5 rest ring: drains in real time; countdown then overtime. */
export function RestRing({ rest }: { rest: NonNullable<SessionView['rest']> }) {
  const r = 110;
  const c = 2 * Math.PI * r;
  const frac = rest.targetS === 0 ? 0 : rest.remainingS / rest.targetS;
  return (
    <div className="relative w-64 h-64 mx-auto" role="timer" aria-live="off" aria-label={rest.done ? `Rest done, ${rest.overtimeS} seconds over` : `${rest.remainingS} seconds of rest left`}>
      <svg viewBox="0 0 240 240" className="w-full h-full -rotate-90">
        <circle cx="120" cy="120" r={r} fill="none" stroke="var(--surface-sunken)" strokeWidth="14" />
        <circle cx="120" cy="120" r={r} fill="none" stroke="var(--now-ink)" strokeWidth="14" strokeLinecap="round" strokeDasharray={c} strokeDashoffset={c * (1 - frac)} />
      </svg>
      <div className="absolute inset-0 grid place-items-center text-center">
        <div>
          <div className="text-text-muted text-sm">{rest.done ? 'Rest done' : rest.getReady ? 'Get ready' : rest.after === 'miss' ? 'Take the full rest' : 'Rest'}</div>
          <div className="num text-num-xl">{rest.done ? `+${mmss(rest.overtimeS)}` : mmss(rest.remainingS)}</div>
          <div className="text-text-muted text-sm">Target {mmss(rest.targetS)}</div>
        </div>
      </div>
    </div>
  );
}

export function Card({ children, className = '' }: { children: ReactNode; className?: string }) {
  return <section className={`bg-surface-raised rounded-[14px] p-4 ${className}`}>{children}</section>;
}
